import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../shared/stores/RootStore";
import { t } from "i18next";
import { defaultNodeScopes } from "../../osaway/stores/OsaNodeStore";

const i18prefix = "map:loading";

export enum FetchingStatus {
  NotStarted = "NotStarted",
  Fetching = "Fetching",
  Done = "Done",
  Error = "Error",
}

enum FetchingStep {
  ProjectsList = "projectsList",
  SelectedProject = "selectedProject",
  Campaigns = "campaigns",
  OsaWays = "osaWays",
  HlaWays = "hlaWays",
  Dashboards = "dashboards",
  ForecastScenarios = "forecastScenarios",
  NodesAndMetadata = "nodesAndMetadata",
}

type FetchingState = Record<FetchingStep, FetchingStatus>;

// TODO manage error and retry logic
export class LoadingStore {
  rootStore;

  // used to cancel running loading processes when the context changes
  latestScope = "Home";

  // natural language text to be shown on the screen
  message: string | undefined;

  fetchingState: FetchingState = {
    projectsList: FetchingStatus.NotStarted,
    selectedProject: FetchingStatus.NotStarted,
    campaigns: FetchingStatus.NotStarted,
    osaWays: FetchingStatus.NotStarted,
    hlaWays: FetchingStatus.NotStarted,
    dashboards: FetchingStatus.NotStarted,
    forecastScenarios: FetchingStatus.NotStarted,
    nodesAndMetadata: FetchingStatus.NotStarted,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  registerScope(scope: string) {
    runInAction(() => {
      this.latestScope = scope;
    });
  }

  isScopeActive(scope: string) {
    return this.latestScope === scope;
  }

  onProjectSelected() {
    this.setFetchingStatus({
      selectedProject: FetchingStatus.NotStarted,
      campaigns: FetchingStatus.NotStarted,
      osaWays: FetchingStatus.NotStarted,
      hlaWays: FetchingStatus.NotStarted,
      dashboards: FetchingStatus.NotStarted,
      forecastScenarios: FetchingStatus.NotStarted,
      nodesAndMetadata: FetchingStatus.NotStarted,
    });
  }

  setFetchingStatus(data: Partial<FetchingState>) {
    runInAction(() => {
      this.fetchingState = { ...this.fetchingState, ...data };
    });
  }

  private setMessage(message: string | undefined) {
    runInAction(() => {
      this.message = message;
    });
  }

  async loadProjectsList(scope: string) {
    await this.runStep(FetchingStep.ProjectsList, scope, async (cancelIf) => {
      const { projectStore } = this.rootStore;
      return projectStore.fetchAllProjects(cancelIf);
    });
  }

  async loadSelectedProject(projectId: string, scope: string) {
    await this.runStep(
      FetchingStep.SelectedProject,
      scope,
      async (cancelIf) => {
        const { projectStore } = this.rootStore;
        projectStore.select(projectId);
        return projectStore.fetchSelectedProject(projectId, cancelIf);
      },
    );
  }

  async loadCampaigns(projectId: string, scope: string) {
    await this.runStep(FetchingStep.Campaigns, scope, async (cancelIf) => {
      const { campaignStore } = this.rootStore;
      return campaignStore.fetchByProject(projectId, cancelIf);
    });
  }

  async loadForecastScenarios(projectId: string, scope: string) {
    await this.runStep(
      FetchingStep.ForecastScenarios,
      scope,
      async (cancelIf) => {
        const { forecastStore } = this.rootStore;
        return forecastStore.fetchForecastScenarios(projectId, cancelIf);
      },
    );
  }

  async loadDashboards(projectId: string, scope: string) {
    await this.runStep(FetchingStep.Dashboards, scope, async (cancelIf) => {
      const { dashboardStore } = this.rootStore;
      return dashboardStore.fetchDashboards(projectId, cancelIf);
    });
  }

  async loadOsaWays(campaignId: string, scope: string) {
    await this.runStep(FetchingStep.OsaWays, scope, async (cancelIf) => {
      const { osaWayStore } = this.rootStore;
      return osaWayStore.fetchAllWays(campaignId, cancelIf);
    });
  }

  async loadOsaNodesAndMetadata(scope?: string) {
    const _scope = scope ?? this.latestScope;

    await this.runStep(
      FetchingStep.NodesAndMetadata,
      _scope,
      async (cancelIf) => {
        const { osaNodeStore, osaCommentStore, osaWayStore, codingStore } =
          this.rootStore;

        for (const way of osaWayStore.ways) {
          this.setMessage(t(`${i18prefix}.nodes`, { name: way.name }));

          await osaNodeStore.fetchNodes(way.id, defaultNodeScopes, cancelIf);

          this.setMessage(t(`${i18prefix}.coding`, { name: way.name }));

          await codingStore.fetchAttributesByOsaWayId(way.id, cancelIf);

          this.setMessage(t(`${i18prefix}.comments`, { name: way.name }));

          if (way.stats!.nActiveComments && way.permissions.canComment) {
            await osaCommentStore.fetchComments(way.id, cancelIf);
          }
        }
      },
    );
  }

  async loadHlaWays(campaignId: string, scope: string) {
    await this.runStep(FetchingStep.HlaWays, scope, async (cancelIf) => {
      const { hlaStore } = this.rootStore;
      return hlaStore.fetchAll(campaignId, cancelIf);
    });
  }

  async runStep(
    step: FetchingStep,
    scope: string,
    fn: (cancelIf: () => boolean) => Promise<void>,
  ) {
    if (this.isStepSkippable(step)) return;
    this.announceStep(step);
    return await this.awaitStep(
      step,
      fn(() => this.cancelIfScopeChanged(step, scope)),
    );
  }

  private cancelIfScopeChanged(step: FetchingStep, scope: string) {
    const scopeChanged = !this.isScopeActive(scope);
    if (scopeChanged) {
      console.log(`Step "${step}" with scope "${scope}" was cancelled.`);
    }
    return scopeChanged;
  }

  private isStepSkippable(step: FetchingStep) {
    if (
      this.fetchingState[step] === FetchingStatus.Done ||
      this.fetchingState[step] === FetchingStatus.Fetching
    ) {
      console.log(
        `Skipping loading step "${step}" as its status is ${this.fetchingState[step]}`,
      );
      return true;
    }
    return false;
  }

  private async announceStep(step: FetchingStep) {
    console.log(`Starting loading step "${step}"`);
    this.setMessage(t(`${i18prefix}.${step}`));
    this.setFetchingStatus({
      [step]: FetchingStatus.Fetching,
    });
  }

  private async awaitStep<T>(step: FetchingStep, promise: Promise<T>) {
    await promise
      .then(() => {
        this.setFetchingStatus({
          [step]: FetchingStatus.Done,
        });
      })
      .catch((e) => {
        this.setFetchingStatus({
          [step]: FetchingStatus.Error,
        });
        console.error(e);
        throw new Error(`Failed to load ${step}`);
      })
      .finally(() => {
        this.setMessage(undefined);
      });
  }
}
